import { CSSProperties, RefObject, useEffect } from "react";

import { INPUT_SUPPORTS_RANGE_SELECTION, INPUT_SUPPORTS_SELECTION } from "./interactive_table.consts";
import { CellCoord, EditTrigger } from "./interactive_table.types";
import { useInteractiveTableStore } from "./interactive_table_store_context";
import { selectCellEditTrigger, selectIsCellInEditMode } from "./interactive_table_store_selectors";

type EditableElement = HTMLElement;

type UseCellInputFocusOptions = {
  directClick?: boolean;
};

export type InputFocusProps = {
  tabIndex: number;
  style: CSSProperties;
};

type UseCellInputFocusReturn = {
  inputFocusProps: InputFocusProps;
};

/**
 * Wire up an input or select element so it participates correctly in the
 * interactive table's edit-mode lifecycle.
 *
 * When the cell enters edit mode:
 * - If trigger is CursorAtEnd (Enter / double-click): focuses the element and
 *   moves the cursor to the end (text inputs) or just focuses (number/select).
 * - If trigger is Overwrite (printable key): focus + select-all is handled
 *   synchronously in InteractiveTable's keydown handler so the triggering character
 *   lands in the input before React re-renders. This hook only re-focuses.
 *
 * The element is rendered with pointer-events:none + tabIndex=-1 outside edit
 * mode, so mouse clicks always hit the <td> first (which sets the anchor) and
 * keyboard Tab is controlled entirely by the grid.
 * Pass { directClick: true } to opt out of the pointer-events guard for
 * elements that should respond to a single click directly (e.g. <select>).
 *
 * @param ref     - ref to the <input> or <select> element
 * @param coord   - the cell coordinate this element belongs to
 * @param options - optional behaviour overrides
 */
export const useCellInputFocus = (
  ref: RefObject<EditableElement | null>,
  coord: CellCoord,
  options: UseCellInputFocusOptions = {}
): UseCellInputFocusReturn => {
  const { directClick = false } = options;
  const isEditMode = useInteractiveTableStore((state) => selectIsCellInEditMode(state.editModeCoord, coord));

  const trigger = useInteractiveTableStore((state) =>
    selectCellEditTrigger(state.editModeCoord, state.editModeTrigger, coord)
  );

  useEffect(() => {
    const el = ref.current;
    if (!isEditMode || !el) {
      return;
    }

    // Focus element inside cell
    el.focus();

    const isTextCell = el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement;

    if (isTextCell) {
      // setSelectionRange is only valid for text-like input types.
      // type="number" (and date, range, etc.) throw a DOMException if called.
      const supportsRangeSelection = INPUT_SUPPORTS_RANGE_SELECTION.includes(el.type);
      if (trigger === EditTrigger.CursorAtEnd && supportsRangeSelection) {
        const len = el.value.length;
        el.setSelectionRange(len, len);
      }
      const supportsSelection = INPUT_SUPPORTS_SELECTION.includes(el.type);
      if (trigger === EditTrigger.Overwrite && supportsSelection) {
        el.select();
      }
    }
  }, [isEditMode, trigger, ref]);

  return {
    /** Props to spread onto the <input> or <select> element. */
    inputFocusProps: {
      tabIndex: isEditMode ? 0 : -1,
      style: {
        pointerEvents: (directClick || isEditMode ? "auto" : "none") as "auto" | "none"
      }
    }
  };
};
